import { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { ImagePlus, Trash2 } from 'lucide-react'

interface Props {
  id: string
  value: string
  onChange: (value: string) => void
}

export default function ContentImageInput({ id, value, onChange }: Props) {
  const inputRef = useRef<HTMLInputElement>(null)
  const valueRef = useRef(value)
  const [uploading, setUploading] = useState(false)

  useEffect(() => { valueRef.current = value }, [value])
  useEffect(() => () => {
    if (valueRef.current) void axios.delete('/api/upload', { data: { path: valueRef.current } }).catch(() => undefined)
  }, [])

  const discardIfUnused = (path: string) => {
    if (path) void axios.delete('/api/upload', { data: { path } }).catch(() => undefined)
  }

  const upload = async (file?: File) => {
    if (!file) return
    setUploading(true)
    try {
      const data = new FormData()
      data.append('contentImage', file)
      const response = await axios.post<{ imageUrl: string }>('/api/upload/content-image', data)
      discardIfUnused(value)
      onChange(response.data.imageUrl)
    } catch (error) {
      toast.error(axios.isAxiosError<{ message?: string }>(error) ? error.response?.data?.message || 'Impossible d’envoyer l’image' : 'Impossible d’envoyer l’image')
    } finally {
      setUploading(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  return (
    <div className="space-y-3">
      <input ref={inputRef} id={id} type="file" accept="image/jpeg,image/png,image/gif,image/webp" className="sr-only" onChange={event => void upload(event.target.files?.[0])} />
      {value && <img src={value} alt="Aperçu" className="h-44 w-full rounded-2xl border border-primary-900/10 bg-white object-cover" />}
      <div className="flex flex-wrap gap-2">
        <button type="button" className="btn-secondary" disabled={uploading} onClick={() => inputRef.current?.click()}>
          <ImagePlus className="h-4 w-4" /> {uploading ? 'Envoi…' : value ? 'Remplacer l’image' : 'Ajouter une image'}
        </button>
        {value && <button type="button" className="btn-danger" onClick={() => { discardIfUnused(value); onChange('') }}><Trash2 className="h-4 w-4" /> Retirer</button>}
      </div>
      <p className="text-xs text-gray-500">JPG, PNG, GIF ou WebP, 10 Mo maximum.</p>
    </div>
  )
}
